

export enum Grade {
  A_PLUS = 'A+',
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  F = 'F',
}

export interface Subject {
  id: string;
  name: string;
  cat1?: number;  // Legacy - kept for backward compatibility
  cat2?: number;  // Legacy - kept for backward compatibility
  exam?: number;  // Legacy - kept for backward compatibility
  scores?: { [examTypeName: string]: number }; // Dynamic exam type scores e.g. { "Mid Term": 45, "Final Exam": 50 }
  score: number; // Total Achieved (Sum of all exam types)
  total: number; // Max Possible (e.g. 100)
  teacherRemarks: string;
}

export interface HeaderSettings {
  isEnabled: boolean;
  layout: 'classic' | 'centered' | 'two-column';
  logo: {
    url: string | null;
    width: number;
    alignment: 'left' | 'center' | 'right';
    padding: number;
    customPositioning: boolean;
    x: number;
    y: number;
  };
  schoolName: {
    text: string;
    fontFamily: string;
    fontSize: number;
    color: string;
    bold: boolean;
    alignment: 'left' | 'center' | 'right';
  };
  address: {
    text: string;
    fontSize: number;
    alignment: 'left' | 'center' | 'right';
    lineHeight: number;
    color: string;
  };
  motto: {
    text: string;
    italic: boolean;
    bold: boolean;
    alignment: 'left' | 'center' | 'right';
    showDivider: boolean;
    color: string;
  };
  spacing: {
    marginTop: number;
    marginBottom: number;
  };
  separator: {
    enabled: boolean;
    color: string;
    thickness: number;
    marginTop: number;
    marginBottom: number;
    horizontalPadding: number;
  };
  scale: number;
}

export interface StudentData {
  name: string;
  studentId: string; // Used as School ID Number
  admissionNumber?: string;
  gradeLevel?: string; // Legacy - kept for backward compatibility
  classLevel?: string; // The class/form the student is in (e.g. "Form 1", "Grade 5")
  stream?: string;
  dob?: string;
  academicYear: string;
  term?: string; // e.g. "Term 1", "Term 2", "Term 3"
  schoolName: string;
  attendancePercentage: number;
  classTeacher: string;
  photoUrl?: string;
  subjects: Subject[];
  examTypes?: { id: string; name: string; weight: number }[]; // List of exam types with full names
  principalRemark: string;
  aiSummary?: string;
  headerSettings?: HeaderSettings;
  position?: number; // Legacy position (mapped to class position usually)
  totalStudents?: number; // Legacy total

  // Specific position fields
  classPosition?: number; // Rank in specific class/stream
  classTotal?: number; // Total in specific class/stream

  overallPosition?: number; // Rank across entire grade/all streams (User calls this "Stream Position")
  overallTotal?: number; // Total across entire grade
}

export const INITIAL_SUBJECTS: Subject[] = [
  { id: '1', name: 'Mathematics', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '2', name: 'Science', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '3', name: 'English Language', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '4', name: 'History', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '5', name: 'Geography', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '6', name: 'Computer Science', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '7', name: 'Art & Design', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '8', name: 'Physical Education', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
];

export const INITIAL_HEADER_SETTINGS: HeaderSettings = {
  isEnabled: false,
  layout: 'classic',
  logo: {
    url: null,
    width: 80,
    alignment: 'left',
    padding: 0,
    customPositioning: false,
    x: 40,
    y: 40
  },
  schoolName: {
    text: 'International Academy of Excellence',
    fontFamily: 'serif',
    fontSize: 28,
    color: '#312e81', // indigo-900
    bold: true,
    alignment: 'left'
  },
  address: {
    text: '123 Academic Avenue, Knowledge City, EduState 54321\nPhone: (555) 123-4567 | Email: contact@academy.edu',
    fontSize: 12,
    alignment: 'left',
    lineHeight: 1.5,
    color: '#64748b' // slate-500
  },
  motto: {
    text: 'Excellence in Education, Character in Life',
    italic: true,
    bold: false,
    alignment: 'left',
    showDivider: true,
    color: '#475569' // slate-600
  },
  spacing: {
    marginTop: 20,
    marginBottom: 20
  },
  separator: {
    enabled: false,
    color: '#e2e8f0',
    thickness: 1,
    marginTop: 10,
    marginBottom: 10,
    horizontalPadding: 0
  },
  scale: 1
};

export const INITIAL_DATA: StudentData = {
  name: '',
  studentId: '',
  admissionNumber: '',
  gradeLevel: '',
  stream: '',
  dob: '',
  academicYear: new Date().getFullYear().toString(),
  schoolName: 'International Academy of Excellence',
  attendancePercentage: 100,
  classTeacher: '',
  photoUrl: '',
  subjects: INITIAL_SUBJECTS,
  principalRemark: '',
  aiSummary: '',
  headerSettings: INITIAL_HEADER_SETTINGS
};

export const SAMPLE_DATA: StudentData = {
  name: 'Alex Morgan',
  studentId: 'ID-882190',
  admissionNumber: 'ADM-2024-089',
  gradeLevel: '10th Grade',
  stream: 'Science A',
  dob: '2008-05-15',
  academicYear: '2024-2025',
  schoolName: 'International Academy of Excellence',
  attendancePercentage: 98,
  classTeacher: 'Mrs. Sarah Jenkins',
  photoUrl: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80',
  subjects: [
    { id: '1', name: 'Mathematics', cat1: 18, cat2: 19, exam: 58, score: 95, total: 100, teacherRemarks: 'Excellent analytical skills and problem solving.' },
    { id: '2', name: 'Science', cat1: 15, cat2: 18, exam: 55, score: 88, total: 100, teacherRemarks: 'Consistent effort shown in lab work.' },
    { id: '3', name: 'English Language', cat1: 19, cat2: 18, exam: 55, score: 92, total: 100, teacherRemarks: 'Outstanding essay writing abilities.' },
    { id: '4', name: 'History', cat1: 16, cat2: 14, exam: 54, score: 84, total: 100, teacherRemarks: 'Participates actively in class discussions.' },
    { id: '5', name: 'Geography', cat1: 14, cat2: 15, exam: 50, score: 79, total: 100, teacherRemarks: 'Needs to improve map reading interpretation.' },
    { id: '6', name: 'Computer Science', cat1: 20, cat2: 20, exam: 58, score: 98, total: 100, teacherRemarks: 'Exceptional programming projects submitted.' },
    { id: '7', name: 'Art & Design', cat1: 18, cat2: 19, exam: 54, score: 91, total: 100, teacherRemarks: 'Very creative and detailed portfolio.' },
    { id: '8', name: 'Physical Education', cat1: 19, cat2: 19, exam: 58, score: 96, total: 100, teacherRemarks: 'Demonstrates great leadership in team sports.' },
  ],
  principalRemark: 'Alex is a diligent student who sets a high standard for peers. The school is proud of these achievements. Keep up the fantastic work!',
  aiSummary: 'Alex has demonstrated outstanding academic performance this term, achieving an overall average that places them in the top percentile of the class.\n\nKey strengths are evident in Mathematics and Computer Science, where Alex shows a natural aptitude for logic and technical application. English and Art are also strong points, highlighting a well-rounded skill set.\n\nTo improve further, focusing on specific technical details in Geography would be beneficial. Overall, this is an exemplary report card that reflects hard work and dedication.',
  headerSettings: {
    ...INITIAL_HEADER_SETTINGS,
    isEnabled: true,
  }
};

export type ReportTemplate =
  | 'default'
  | 'minimal'
  | 'formal'
  | 'corporate'
  | 'creative'
  | 'tech'
  | 'elegant'
  | 'nature'
  | 'bold'
  | 'sidebar'
  | 'grid'
  | 'rose'
  | 'blossom'
  | 'royal'
  | 'slate'
  | 'mint'
  | 'oceanic'
  | 'crimson'
  | 'sunset'
  | 'glass'
  | 'classic-blue';

export const TEMPLATES: { id: ReportTemplate, name: string, description: string }[] = [
  { id: 'default', name: 'Default Indigo', description: 'Clean and professional.' },
  { id: 'rose', name: 'Rose Gold Elegant', description: 'Sophisticated pink and slate tones.' },
  { id: 'blossom', name: 'Cherry Blossom', description: 'Soft, airy pinks with rounded geometry.' },
  { id: 'royal', name: 'Royal Gold', description: 'Prestigious navy and gold academic style.' },
  { id: 'slate', name: 'Modern Slate', description: 'High contrast grey and black professional.' },
  { id: 'mint', name: 'Fresh Mint', description: 'Calming teal and mint greens.' },
  { id: 'oceanic', name: 'Deep Ocean', description: 'Professional blues and cyans.' },
  { id: 'crimson', name: 'Ivy League Crimson', description: 'Traditional academic red.' },
  { id: 'sunset', name: 'Warm Sunset', description: 'Gradient headers orange to rose.' },
  { id: 'glass', name: 'Glassmorphism', description: 'Trendy frosted glass effects.' },
  { id: 'classic-blue', name: 'Certificate Blue', description: 'Old-school certificate borders.' },
  { id: 'minimal', name: 'Modern Minimal', description: 'Clean, black and white, no distractions.' },
  { id: 'formal', name: 'Classic Formal', description: 'Traditional serif, academic borders.' },
  { id: 'corporate', name: 'Corporate Blue', description: 'Structured, navy headers, striped rows.' },
  { id: 'creative', name: 'Creative Spark', description: 'Playful colors and rounded geometry.' },
  { id: 'tech', name: 'Tech Matrix', description: 'Monospace font, digital aesthetics.' },
  { id: 'elegant', name: 'Warm Elegant', description: 'Sophisticated cream and gold palette.' },
  { id: 'nature', name: 'Eco Nature', description: 'Calming greens and soft corners.' },
  { id: 'bold', name: 'Bold Impact', description: 'High contrast, strong red accents.' },
  { id: 'sidebar', name: 'Sidebar Layout', description: 'Split layout with student info on the left.' },
  { id: 'grid', name: 'Grid Cards', description: 'Subjects displayed in a card grid.' },
];

export const ID_PATTERNS = [
  { id: 'none', name: 'No Pattern' },
  { id: 'watercolor', name: 'Ink Wash' },
  { id: 'brush', name: 'Rough Brush' },
  { id: 'waves-soft', name: 'Soft Smoke' },
  { id: 'splatter', name: 'Ink Splatter' },
  { id: 'marble', name: 'Liquid Marble' },
  { id: 'crosshatch', name: 'Paint Fog' },
  { id: 'noise', name: 'Spray Noise' },
  { id: 'halftone', name: 'Halftone Fade' },
  { id: 'grid', name: 'Security Grid' },
  { id: 'dots', name: 'Micro Dots' },
  { id: 'hexagons', name: 'Hexagon Mesh' },
  { id: 'waves', name: 'Guilloche Waves' },
  { id: 'lines', name: 'Diagonal Texture' },
  { id: 'circuit', name: 'Tech Circuit' },
  { id: 'geometric', name: 'Geometric Shards' },
  { id: 'topo', name: 'Topographic Map' },
  { id: 'canvas', name: 'Canvas Weave' },
];

export const AI_STYLES = [
  { id: 'none', name: 'None' },
  { id: 'abstract', name: 'Abstract Fluid' },
  { id: 'geometric', name: 'Geometric Shapes' },
  { id: 'watercolor', name: 'Watercolor Art' },
  { id: 'minimalist', name: 'Minimalist Clean' },
  { id: 'sci-fi', name: 'Futuristic Sci-Fi' },
  { id: 'nature', name: 'Organic Nature' },
  { id: 'texture', name: 'Paper Texture' },
  { id: 'oil', name: 'Oil Painting' },
];

export const AI_THEMES = [
  { id: 'none', name: 'None' },
  { id: 'serene', name: 'Serene & Calm' },
  { id: 'vibrant', name: 'Vibrant & Bold' },
  { id: 'professional', name: 'Corporate Professional' },
  { id: 'dark', name: 'Dark & Elegant' },
  { id: 'playful', name: 'Playful & Bright' },
  { id: 'academic', name: 'Classic Academic' },
  { id: 'luxury', name: 'Luxury & Gold' },
];

export const ID_CARD_DESIGNS = [
  { id: 'none', name: 'None (Basic)', category: 'Basic' },

  // Modern Layout (Standard)
  { id: 'modern-indigo', name: 'Modern Indigo', category: 'Modern' },
  { id: 'modern-crimson', name: 'Modern Crimson', category: 'Modern' },
  { id: 'modern-teal', name: 'Modern Teal', category: 'Modern' },
  { id: 'modern-slate', name: 'Modern Slate', category: 'Modern' },
  { id: 'modern-violet', name: 'Modern Violet', category: 'Modern' },
  { id: 'modern-amber', name: 'Modern Amber', category: 'Modern' },
  { id: 'modern-emerald', name: 'Modern Emerald', category: 'Modern' },
  { id: 'modern-royal', name: 'Modern Royal', category: 'Modern' },
  { id: 'modern-sky', name: 'Modern Sky', category: 'Modern' },
  { id: 'modern-rose', name: 'Modern Rose', category: 'Modern' },

  // Sidebar Layout (Left Bar)
  { id: 'sidebar-navy', name: 'Sidebar Navy', category: 'Sidebar' },
  { id: 'sidebar-maroon', name: 'Sidebar Maroon', category: 'Sidebar' },
  { id: 'sidebar-forest', name: 'Sidebar Forest', category: 'Sidebar' },
  { id: 'sidebar-charcoal', name: 'Sidebar Charcoal', category: 'Sidebar' },
  { id: 'sidebar-plum', name: 'Sidebar Plum', category: 'Sidebar' },
  { id: 'sidebar-bronze', name: 'Sidebar Bronze', category: 'Sidebar' },
  { id: 'sidebar-ocean', name: 'Sidebar Ocean', category: 'Sidebar' },
  { id: 'sidebar-berry', name: 'Sidebar Berry', category: 'Sidebar' },
  { id: 'sidebar-gold', name: 'Sidebar Gold', category: 'Sidebar' },
  { id: 'sidebar-steel', name: 'Sidebar Steel', category: 'Sidebar' },

  // Hero Layout (Header Bar)
  { id: 'hero-cobalt', name: 'Hero Cobalt', category: 'Hero' },
  { id: 'hero-ruby', name: 'Hero Ruby', category: 'Hero' },
  { id: 'hero-pine', name: 'Hero Pine', category: 'Hero' },
  { id: 'hero-graphite', name: 'Hero Graphite', category: 'Hero' },
  { id: 'hero-amethyst', name: 'Hero Amethyst', category: 'Hero' },
  { id: 'hero-copper', name: 'Hero Copper', category: 'Hero' },
  { id: 'hero-azure', name: 'Hero Azure', category: 'Hero' },
  { id: 'hero-coral', name: 'Hero Coral', category: 'Hero' },
  { id: 'hero-citrine', name: 'Hero Citrine', category: 'Hero' },
  { id: 'hero-silver', name: 'Hero Silver', category: 'Hero' },

  // Diagonal Layout (Geometric)
  { id: 'diagonal-midnight', name: 'Diagonal Midnight', category: 'Diagonal' },
  { id: 'diagonal-cardinal', name: 'Diagonal Cardinal', category: 'Diagonal' },
  { id: 'diagonal-moss', name: 'Diagonal Moss', category: 'Diagonal' },
  { id: 'diagonal-jet', name: 'Diagonal Jet', category: 'Diagonal' },
  { id: 'diagonal-orchid', name: 'Diagonal Orchid', category: 'Diagonal' },
  { id: 'diagonal-sand', name: 'Diagonal Sand', category: 'Diagonal' },
  { id: 'diagonal-aqua', name: 'Diagonal Aqua', category: 'Diagonal' },
  { id: 'diagonal-fuchsia', name: 'Diagonal Fuchsia', category: 'Diagonal' },
  { id: 'diagonal-lime', name: 'Diagonal Lime', category: 'Diagonal' },
  { id: 'diagonal-ash', name: 'Diagonal Ash', category: 'Diagonal' },

  // Full Layout (Split Color)
  { id: 'full-primary', name: 'Full Primary', category: 'Full' },
  { id: 'full-alert', name: 'Full Alert', category: 'Full' },
  { id: 'full-nature', name: 'Full Nature', category: 'Full' },
  { id: 'full-dark', name: 'Full Dark', category: 'Full' },
  { id: 'full-luxury', name: 'Full Luxury', category: 'Full' },
  { id: 'full-earth', name: 'Full Earth', category: 'Full' },
  { id: 'full-water', name: 'Full Water', category: 'Full' },
  { id: 'full-fire', name: 'Full Fire', category: 'Full' },
  { id: 'full-air', name: 'Full Air', category: 'Full' },
  { id: 'full-metal', name: 'Full Metal', category: 'Full' },
];